import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'sidebar_width';
const MIN_WIDTH = 65;
/** Default expanded sidebar width (~15% of viewport). */
const DEFAULT_WIDTH_FRACTION = 0.15;
/** Maximum drag width (~25% of viewport). */
const MAX_WIDTH_FRACTION = 0.25;

function getDefaultWidth() {
    return Math.round(window.innerWidth * DEFAULT_WIDTH_FRACTION);
}

function getMaxWidth() {
    return Math.round(window.innerWidth * MAX_WIDTH_FRACTION);
}

function clamp(value: number, min: number, max: number) {
    return Math.min(Math.max(value, min), max);
}

function loadWidth(): number {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const parsed = parseInt(stored, 10);
            if (!isNaN(parsed) && parsed >= MIN_WIDTH) return parsed;
        }
    } catch {
        /* noop */
    }
    return getDefaultWidth();
}

export function useResizableSidebar() {
    const [width, setWidth] = useState(loadWidth);
    const dragging = useRef(false);
    const startX = useRef(0);
    const startWidth = useRef(0);

    const collapsed = width <= MIN_WIDTH + 10;

    const onMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            dragging.current = true;
            startX.current = e.clientX;
            startWidth.current = width;
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        },
        [width],
    );

    useEffect(() => {
        const onMouseMove = (e: MouseEvent) => {
            if (!dragging.current) return;
            const delta = e.clientX - startX.current;
            const next = clamp(startWidth.current + delta, MIN_WIDTH, getMaxWidth());
            setWidth(next);
        };

        const onMouseUp = () => {
            if (!dragging.current) return;
            dragging.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            setWidth(prev => {
                try {
                    localStorage.setItem(STORAGE_KEY, String(prev));
                } catch {
                    /* noop */
                }
                return prev;
            });
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, []);

    return { width, collapsed, onMouseDown, MIN_WIDTH, getMaxWidth };
}
